// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import './styles/main.css';
import { registerSW } from 'virtual:pwa-register';
import { createUI } from './ui';
import type { UI } from './ui';
import { Ditherer, isAbort } from './ditherer';
import type { AlgoId, DitherParams, DitherResult, PaletteId } from './types';
import { DEFAULT_PRESET_ID, paramsFromPreset } from './presets';
import { clampInt } from './dither';
import { isAcceptedImage } from './imageutil';
import { emit } from './eventlog';

const SAMPLE_URL = `${import.meta.env.BASE_URL}samples/sample-plaza.jpg`;
const STORE_KEY = 'ditherwell.params.v1';
const MAX_INPUT_BYTES = 40 * 1024 * 1024;

const root = document.getElementById('app')!;
const ditherer = new Ditherer();

let ui: UI;
let currentFile: File | null = null;
let rasterUrl: string | null = null;
let outUrl: string | null = null;
let lastResult: DitherResult | null = null;
let debounceTimer = 0;

function main(): void {
  ui = createUI(root, {
    onFile: handleFile,
    onSample: handleSample,
    onChange: scheduleDither,
    onReset: reset,
    onDownload: download,
    onCopy: copyPng,
    onShare: share,
  });

  const saved = loadParams();
  ui.setParams(saved ?? paramsFromPreset(DEFAULT_PRESET_ID));

  ui.showStage('empty');
  ui.setShareVisible(shareSupported());

  emit('system', 'ok', 'Ditherwell ready — drop an image to begin.');
  registerSW({ immediate: true });
}

// ── File ingestion ─────────────────────────────────────────────────────
function handleFile(file: File): void {
  if (!isAcceptedImage(file)) {
    ui.setError(`"${file.name || 'That file'}" is not a supported image (PNG, JPG, WEBP, GIF, BMP).`);
    emit('input', 'err', 'Rejected unsupported file', { name: file.name, type: file.type });
    return;
  }
  if (file.size > MAX_INPUT_BYTES) {
    ui.setError('That image is over 40 MB — please pick a smaller one.');
    emit('input', 'err', 'Rejected oversized file', { bytes: file.size });
    return;
  }
  ui.clearError();
  currentFile = file;
  if (rasterUrl) URL.revokeObjectURL(rasterUrl);
  rasterUrl = URL.createObjectURL(file);
  ui.setFileName(file.name || 'pasted image');
  ui.showStage('working');
  emit('input', 'ok', 'Image loaded', {
    name: file.name || 'pasted',
    size: `${(file.size / 1024).toFixed(0)} KB`,
  });
  runDither();
}

async function handleSample(): Promise<void> {
  try {
    emit('input', 'info', 'Loading sample image…');
    const res = await fetch(SAMPLE_URL);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const file = new File([blob], 'sample-plaza.jpg', { type: blob.type || 'image/jpeg' });
    handleFile(file);
  } catch (err) {
    ui.setError('Could not load the sample image. Try picking a file instead.');
    emit('input', 'err', 'Sample load failed', { error: String(err) });
  }
}

// ── Dithering ──────────────────────────────────────────────────────────
function scheduleDither(): void {
  saveParams();
  if (!currentFile) return;
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(runDither, 200);
}

async function runDither(): Promise<void> {
  if (!currentFile) return;
  const params = ui.getParams();
  ui.clearError();
  ui.setBusy(true, 'Decoding…', 5);
  emit('dither', 'info', 'Dithering…', {
    palette: params.palette,
    algo: params.algorithm,
    px: params.pixelSize,
  });

  try {
    const res = await ditherer.dither(currentFile, params, (p) => {
      if (p.phase === 'decode') ui.setBusy(true, 'Decoding…', p.pct);
      else if (p.phase === 'dither') ui.setBusy(true, 'Dithering…', p.pct);
      else ui.setBusy(true, 'Rendering…', p.pct);
    });
    lastResult = res;
    if (outUrl) URL.revokeObjectURL(outUrl);
    outUrl = URL.createObjectURL(res.blob);
    ui.showResult(res, outUrl, rasterUrl!);
    ui.setBusy(false);
    emit('output', 'ok', 'Dithered', {
      size: `${res.width}×${res.height}`,
      colours: res.colors,
      png: `${(res.bytes / 1024).toFixed(1)} KB`,
      ms: res.ms,
    });
  } catch (err) {
    if (isAbort(err)) return; // superseded by a newer job
    ui.setBusy(false);
    ui.setError(err instanceof Error ? err.message : 'Dithering failed.');
    emit('dither', 'err', 'Dither failed', { error: String(err) });
  }
}

// ── Output actions ─────────────────────────────────────────────────────
function baseName(): string {
  const n = currentFile?.name || 'ditherwell';
  return (n.replace(/\.[^.]+$/, '') || 'ditherwell') + '-dither';
}

async function download(): Promise<void> {
  if (!lastResult) return;
  const name = `${baseName()}.png`;
  const fsa = (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker;
  if (typeof fsa === 'function') {
    try {
      const handle = await (
        fsa as (o: unknown) => Promise<{
          createWritable: () => Promise<{
            write: (d: Blob) => Promise<void>;
            close: () => Promise<void>;
          }>;
        }>
      )({
        suggestedName: name,
        types: [{ description: 'PNG image', accept: { 'image/png': ['.png'] } }],
      });
      const w = await handle.createWritable();
      await w.write(lastResult.blob);
      await w.close();
      ui.toast('Saved PNG');
      emit('output', 'ok', 'Saved PNG', { name });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return; // user cancelled
      // fall through to anchor download
    }
  }
  anchorDownload(lastResult.blob, name);
  ui.toast('Downloaded PNG');
  emit('output', 'ok', 'Downloaded PNG', { name });
}

function anchorDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function copyPng(): Promise<void> {
  if (!lastResult) return;
  try {
    if (typeof ClipboardItem === 'undefined') throw new Error('no ClipboardItem');
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': lastResult.blob })]);
    ui.toast('PNG copied');
    emit('output', 'ok', 'Copied PNG');
  } catch {
    ui.setError('Copy is not available in this browser. Use Download instead.');
  }
}

function shareSupported(): boolean {
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (!nav.share || !nav.canShare) return false;
  try {
    const probe = new File([new Uint8Array([137, 80, 78, 71])], 'probe.png', { type: 'image/png' });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

async function share(): Promise<void> {
  if (!lastResult) return;
  const file = new File([lastResult.blob], `${baseName()}.png`, { type: 'image/png' });
  try {
    await (navigator as Navigator).share({ files: [file], title: 'Ditherwell' });
    emit('output', 'ok', 'Shared PNG');
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    ui.setError('Sharing failed on this device.');
  }
}

// ── Reset & persistence ────────────────────────────────────────────────
function reset(): void {
  ditherer.cancel();
  currentFile = null;
  lastResult = null;
  if (rasterUrl) URL.revokeObjectURL(rasterUrl);
  if (outUrl) URL.revokeObjectURL(outUrl);
  rasterUrl = null;
  outUrl = null;
  ui.clearError();
  ui.setBusy(false);
  ui.showStage('empty');
  emit('ui', 'info', 'Cleared — ready for a new image.');
}

function saveParams(): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(ui.getParams()));
  } catch {
    /* storage may be unavailable; non-fatal */
  }
}

function loadParams(): DitherParams | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return null;
    const palettes: PaletteId[] = ['mono', 'gray', 'gameboy', 'cga', 'sepia', 'adaptive'];
    const algos: AlgoId[] = ['floyd', 'atkinson', 'jjn', 'stucki', 'bayer4', 'bayer8', 'none'];
    return {
      palette: palettes.includes(p.palette) ? p.palette : 'mono',
      colors: clampInt(p.colors, 2, 32),
      algorithm: algos.includes(p.algorithm) ? p.algorithm : 'floyd',
      pixelSize: clampInt(p.pixelSize, 1, 12),
      brightness: clampInt(p.brightness, -100, 100),
      contrast: clampInt(p.contrast, -100, 100),
      serpentine: !!p.serpentine,
    };
  } catch {
    return null;
  }
}

// Free object URLs on unload.
window.addEventListener('pagehide', () => {
  if (rasterUrl) URL.revokeObjectURL(rasterUrl);
  if (outUrl) URL.revokeObjectURL(outUrl);
});

main();
